// src/components/catalog/CatalogSettings.jsx
import React, { useState } from 'react';
import { 
  X, 
  Upload, 
  Image as ImageIcon, 
  Trash2, 
  Eye, 
  Grid, 
  Link,
  Type,
  Palette,
  Settings,
  Globe,
  Store,
  Phone,
  MessageCircle,
  Mail,
  ExternalLink,
  Save,
  Monitor,
  Smartphone,
  Tablet
} from 'lucide-react';
import { catalogService } from '../../services/catalogService';

export const CatalogSettingsModal = ({ catalog, onSave, onClose }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    // Basic Information
    botName: catalog.botName || '',
    description: catalog.description || '',
    botCategory: catalog.botCategory || 'catalog',
    
    // Display & Grid
    gridColumns: catalog.gridColumns || 3,
    showItemsInModal: catalog.showItemsInModal !== false,
    allowExternalLinks: catalog.allowExternalLinks !== false,
    
    // Styling & Colors
    primaryColor: catalog.primaryColor || '#0795ff',
    backgroundColor: catalog.backgroundColor || '#f8fafc',
    headerColor: catalog.headerColor || '#ffffff',
    accentColor: catalog.accentColor || '#8b5cf6',
    fontFamily: catalog.fontFamily || 'Helvetica, sans-serif',
    
    // Company & Contact
    companyName: catalog.companyName || '',
    whatsappNumber: catalog.whatsappNumber || '',
    contactNumber: catalog.contactNumber || '',
    email: catalog.email || '',
    website: catalog.website || '',
    
    // Images
    coverImageUrl: catalog.coverImageUrl || '',
    logoUrl: catalog.logoUrl || '',
    
    // Publishing & Access
    published: catalog.published !== false,
    accessLevel: catalog.accessLevel || 'public',
    passwordProtected: catalog.passwordProtected || false,
    password: catalog.password || '',
    
    // SEO & Custom Domain
    customDomain: catalog.customDomain || '',
    seoTitle: catalog.seoTitle || '',
    seoDescription: catalog.seoDescription || '',
    seoKeywords: catalog.seoKeywords || '',
    ogImage: catalog.ogImage || '',
    
    // Advanced Features
    enableAnalytics: catalog.enableAnalytics !== false,
    allowSearch: catalog.allowSearch !== false,
    allowFiltering: catalog.allowFiltering !== false,
    enableSorting: catalog.enableSorting !== false,
    autoGenerateTags: catalog.autoGenerateTags || false,
    
    // Interaction Features
    enableWishlist: catalog.enableWishlist || false,
    allowSharing: catalog.allowSharing !== false,
    showPrices: catalog.showPrices !== false,
    enableInquiries: catalog.enableInquiries !== false
  });

  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(catalog.coverImageUrl || '');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(catalog.logoUrl || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCoverImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCoverImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setCoverImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleLogoSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadImages = async () => {
    const updates = {};
    
    try {
      if (coverImageFile) {
        const coverUrl = await catalogService.uploadImage(coverImageFile, `catalogs/${catalog.id}/cover`);
        updates.coverImageUrl = coverUrl;
      }
      
      if (logoFile) {
        const logoUrl = await catalogService.uploadImage(logoFile, `catalogs/${catalog.id}/logo`);
        updates.logoUrl = logoUrl;
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    }
    
    return updates;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const imageUpdates = await uploadImages();
      const updatedData = { ...formData, ...imageUpdates };
      
      await onSave(updatedData);
      onClose();
    } catch (error) {
      console.error('Error saving catalog settings:', error);
      alert('Failed to save catalog settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const generatePreviewUrl = () => {
    if (formData.customDomain) {
      return `https://${formData.customDomain}`;
    }
    return `${window.location.origin}/books/${catalog.id}`;
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Type },
    { id: 'display', label: 'Display & Grid', icon: Grid },
    { id: 'styling', label: 'Styling & Colors', icon: Palette },
    { id: 'images', label: 'Images & Media', icon: ImageIcon },
    { id: 'contact', label: 'Company & Contact', icon: Store },
    { id: 'features', label: 'Features', icon: Settings },
    { id: 'publishing', label: 'Publishing & Access', icon: Globe },
    { id: 'seo', label: 'SEO & Domain', icon: ExternalLink },
    { id: 'preview', label: 'Preview', icon: Eye }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex">
        {/* Sidebar Navigation */}
        <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="bg-orange-100 p-2 rounded-lg">
                <Store className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Catalog Settings</h2>
                <p className="text-sm text-gray-600">Configure your catalog</p>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex-1 overflow-y-auto p-4">
            <nav className="space-y-1">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors text-sm
                    ${activeTab === tab.id 
                      ? 'bg-orange-100 text-orange-700 font-medium' 
                      : 'text-gray-600 hover:bg-gray-100'
                    }
                  `}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Footer Actions */}
          <div className="p-4 border-t border-gray-200 space-y-2">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {/* Basic Info Tab */}
            {activeTab === 'basic' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Catalog Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.botName}
                        onChange={(e) => handleInputChange('botName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Enter catalog name..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Describe your catalog..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        value={formData.botCategory}
                        onChange={(e) => handleInputChange('botCategory', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="catalog">General Catalog</option>
                        <option value="retail">Retail Products</option>
                        <option value="automotive">Automotive</option>
                        <option value="electronics">Electronics</option>
                        <option value="fashion">Fashion & Apparel</option>
                        <option value="food">Food & Beverage</option>
                        <option value="home">Home & Garden</option>
                        <option value="services">Services</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Display & Grid Tab */}
            {activeTab === 'display' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Display & Grid Options</h3>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Grid Columns
                      </label>
                      <select
                        value={formData.gridColumns}
                        onChange={(e) => handleInputChange('gridColumns', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value={2}>2 Columns</option>
                        <option value={3}>3 Columns</option>
                        <option value={4}>4 Columns</option>
                        <option value={5}>5 Columns</option>
                        <option value={6}>6 Columns</option>
                      </select>
                      <p className="text-sm text-gray-500 mt-1">
                        Number of columns in grid view (responsive on smaller screens)
                      </p>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Item Display Options</h4>
                      
                      {[
                        { 
                          key: 'showItemsInModal', 
                          label: 'Open items in modal popup', 
                          desc: 'When enabled, clicking items opens them in a modal instead of navigating away' 
                        },
                        { 
                          key: 'allowExternalLinks', 
                          label: 'Allow external product links', 
                          desc: 'Enable adding external links to products (websites, more details, etc.)' 
                        },
                        { 
                          key: 'showPrices', 
                          label: 'Show product prices', 
                          desc: 'Display price information for catalog items' 
                        },
                        { 
                          key: 'allowSearch', 
                          label: 'Enable search functionality', 
                          desc: 'Allow users to search through catalog items' 
                        },
                        { 
                          key: 'allowFiltering', 
                          label: 'Enable category filtering', 
                          desc: 'Allow users to filter items by category or tags' 
                        },
                        { 
                          key: 'enableSorting', 
                          label: 'Enable sorting options', 
                          desc: 'Allow users to sort items by price, name, date, etc.' 
                        }
                      ].map(({ key, label, desc }) => (
                        <label key={key} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData[key]}
                            onChange={(e) => handleInputChange(key, e.target.checked)}
                            className="mt-1 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{label}</div>
                            <div className="text-sm text-gray-500">{desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Styling & Colors Tab */}
            {activeTab === 'styling' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Colors & Styling</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Primary Color
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={formData.primaryColor}
                          onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                          className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={formData.primaryColor}
                          onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Background Color
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={formData.backgroundColor}
                          onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                          className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={formData.backgroundColor}
                          onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Header Color
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={formData.headerColor}
                          onChange={(e) => handleInputChange('headerColor', e.target.value)}
                          className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={formData.headerColor}
                          onChange={(e) => handleInputChange('headerColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Accent Color
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={formData.accentColor}
                          onChange={(e) => handleInputChange('accentColor', e.target.value)}
                          className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={formData.accentColor}
                          onChange={(e) => handleInputChange('accentColor', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Font Family
                    </label>
                    <select
                      value={formData.fontFamily}
                      onChange={(e) => handleInputChange('fontFamily', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="Helvetica, sans-serif">Helvetica</option>
                      <option value="Arial, sans-serif">Arial</option>
                      <option value="Georgia, serif">Georgia</option>
                      <option value="Times New Roman, serif">Times New Roman</option>
                      <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                      <option value="Verdana, sans-serif">Verdana</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Images & Media Tab */}
            {activeTab === 'images' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Images & Media</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Cover Image */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Cover Image
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                        {coverImagePreview ? (
                          <div className="relative">
                            <img
                              src={coverImagePreview}
                              alt="Cover preview"
                              className="w-full h-48 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setCoverImagePreview('');
                                setCoverImageFile(null);
                                handleInputChange('coverImageUrl', '');
                              }}
                              className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="text-center">
                            <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <label className="cursor-pointer">
                              <span className="text-blue-600 hover:text-blue-500 font-medium">
                                Upload cover image
                              </span>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleCoverImageSelect}
                                className="hidden"
                              />
                            </label>
                            <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Logo */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Company Logo
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                        {logoPreview ? (
                          <div className="relative">
                            <img
                              src={logoPreview}
                              alt="Logo preview"
                              className="w-full h-48 object-contain rounded-lg bg-gray-50"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setLogoPreview('');
                                setLogoFile(null);
                                handleInputChange('logoUrl', '');
                              }}
                              className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="text-center">
                            <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <label className="cursor-pointer">
                              <span className="text-blue-600 hover:text-blue-500 font-medium">
                                Upload logo
                              </span>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleLogoSelect}
                                className="hidden"
                              />
                            </label>
                            <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 2MB</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Company & Contact Tab */}
            {activeTab === 'contact' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Company & Contact Information</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={formData.companyName}
                        onChange={(e) => handleInputChange('companyName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Your company name"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <MessageCircle className="h-4 w-4 inline mr-1" />
                          WhatsApp Number
                        </label>
                        <input
                          type="tel"
                          value={formData.whatsappNumber}
                          onChange={(e) => handleInputChange('whatsappNumber', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="+27123456789"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Phone className="h-4 w-4 inline mr-1" />
                          Contact Number
                        </label>
                        <input
                          type="tel"
                          value={formData.contactNumber}
                          onChange={(e) => handleInputChange('contactNumber', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="+27123456789"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Mail className="h-4 w-4 inline mr-1" />
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="contact@company.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <ExternalLink className="h-4 w-4 inline mr-1" />
                          Website
                        </label>
                        <input
                          type="url"
                          value={formData.website}
                          onChange={(e) => handleInputChange('website', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="https://www.company.com"
                        />
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2">Contact Integration</h4>
                      <p className="text-sm text-blue-700">
                        Contact buttons will appear on catalog items when contact information is provided. 
                        Users can quickly reach out via WhatsApp, phone, or email directly from product pages.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Features Tab */}
            {activeTab === 'features' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Advanced Features</h3>
                  
                  <div className="space-y-4">
                    {[
                      { 
                        key: 'enableAnalytics', 
                        label: 'Enable Analytics', 
                        desc: 'Track page views, item clicks, and user engagement' 
                      },
                      { 
                        key: 'enableWishlist', 
                        label: 'Enable Wishlist', 
                        desc: 'Allow users to save items to a wishlist for later' 
                      },
                      { 
                        key: 'allowSharing', 
                        label: 'Allow Social Sharing', 
                        desc: 'Users can share catalog items on social media' 
                      },
                      { 
                        key: 'enableInquiries', 
                        label: 'Enable Product Inquiries', 
                        desc: 'Allow users to send inquiries about specific products' 
                      },
                      { 
                        key: 'autoGenerateTags', 
                        label: 'Auto-Generate Tags', 
                        desc: 'Automatically generate tags based on product information' 
                      }
                    ].map(({ key, label, desc }) => (
                      <label key={key} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData[key]}
                          onChange={(e) => handleInputChange(key, e.target.checked)}
                          className="mt-1 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{label}</div>
                          <div className="text-sm text-gray-500">{desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Publishing & Access Tab */}
            {activeTab === 'publishing' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Publishing & Access Control</h3>
                  
                  {/* Publishing Status */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <label className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={formData.published}
                        onChange={(e) => handleInputChange('published', e.target.checked)}
                        className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <div className="font-medium text-blue-900">Publish Catalog</div>
                        <div className="text-sm text-blue-700">Make this catalog publicly accessible</div>
                      </div>
                    </label>
                  </div>

                  {/* Access Level */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Access Level
                      </label>
                      <select
                        value={formData.accessLevel}
                        onChange={(e) => handleInputChange('accessLevel', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="public">Public - Anyone can view</option>
                        <option value="unlisted">Unlisted - Only with link</option>
                        <option value="private">Private - Owner only</option>
                      </select>
                    </div>

                    {/* Password Protection */}
                    <div className="space-y-3">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.passwordProtected}
                          onChange={(e) => handleInputChange('passwordProtected', e.target.checked)}
                          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Password Protection</span>
                      </label>
                      
                      {formData.passwordProtected && (
                        <input
                          type="password"
                          value={formData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="Enter password"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* SEO & Domain Tab */}
            {activeTab === 'seo' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">SEO & Custom Domain</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom Domain
                      </label>
                      <input
                        type="text"
                        value={formData.customDomain}
                        onChange={(e) => handleInputChange('customDomain', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="catalog.yourdomain.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Optional: Use your own domain for this catalog
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SEO Title
                      </label>
                      <input
                        type="text"
                        value={formData.seoTitle}
                        onChange={(e) => handleInputChange('seoTitle', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Custom title for search engines"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SEO Description
                      </label>
                      <textarea
                        value={formData.seoDescription}
                        onChange={(e) => handleInputChange('seoDescription', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Description for search engine results"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SEO Keywords
                      </label>
                      <input
                        type="text"
                        value={formData.seoKeywords}
                        onChange={(e) => handleInputChange('seoKeywords', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="catalog, products, shopping, commerce"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Preview Tab */}
            {activeTab === 'preview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview & Testing</h3>
                  
                  <div className="space-y-4">
                    {/* Preview URL */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">Catalog URL</h4>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-white px-3 py-2 rounded border text-sm">
                          {generatePreviewUrl()}
                        </code>
                        <button
                          type="button"
                          onClick={() => window.open(generatePreviewUrl(), '_blank')}
                          className="px-3 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Device Preview */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Device Preview</h4>
                      <div className="flex gap-2">
                        <button className="flex items-center gap-2 px-3 py-2 bg-gray-200 rounded text-sm">
                          <Monitor className="h-4 w-4" />
                          Desktop
                        </button>
                        <button className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded text-sm">
                          <Tablet className="h-4 w-4" />
                          Tablet
                        </button>
                        <button className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded text-sm">
                          <Smartphone className="h-4 w-4" />
                          Mobile
                        </button>
                      </div>
                    </div>

                    {/* Preview Features */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h5 className="font-medium text-gray-900 mb-2">Grid Layout</h5>
                        <p className="text-sm text-gray-600">
                          {formData.gridColumns} columns on desktop
                        </p>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h5 className="font-medium text-gray-900 mb-2">Contact Options</h5>
                        <p className="text-sm text-gray-600">
                          {[formData.whatsappNumber, formData.contactNumber, formData.email].filter(Boolean).length} methods
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogSettingsModal;